import { useCallback, useMemo, useRef, useState } from "react";
import { ImportSourceSection } from "../components/ImportSourceSection";
import { NormalisationBlockedPanel } from "../components/NormalisationBlockedPanel";
import { NormalisationCompletePanel } from "../components/NormalisationCompletePanel";
import { NormalisationProgressPanel } from "../components/NormalisationProgressPanel";
import { PayRunPdfUploadSection } from "../components/PayRunPdfUploadSection";
import { useClientPayImportSources } from "../hooks/useClientPayImportSources";
import { useClientPayNormalisationArtifacts } from "../hooks/useClientPayNormalisationArtifacts";
import { useClientPayNormalisationRun } from "../hooks/useClientPayNormalisationRun";
import {
  isNormalisationRunBlocked,
  isNormalisationRunSuccess,
} from "../types/normalisationRun";
import { getClientPayPdfFileRejection } from "../utils";
import s from "./ClientPayNormalisationScreen.module.css";

/** Main content for client pay-run normalisation (CN-01–CN-08). */
export function ClientPayNormalisationScreen() {
  const uploadSectionRef = useRef<HTMLElement>(null);
  const {
    data: sources = [],
    isLoading: isCatalogueLoading,
    isError: isCatalogueError,
    error: catalogueError,
  } = useClientPayImportSources();
  const catalogueLoadError =
    isCatalogueError && catalogueError instanceof Error
      ? catalogueError.message
      : null;
  const normaliseRun = useClientPayNormalisationRun();
  const artifacts = useClientPayNormalisationArtifacts();

  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const [payPeriodLabel, setPayPeriodLabel] = useState("");
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [fileTypeError, setFileTypeError] = useState<string | null>(null);

  const selectedSource = useMemo(
    () => sources.find((source) => source.id === selectedSourceId) ?? null,
    [sources, selectedSourceId],
  );

  const runResult = normaliseRun.data;

  const activeImportSourceId =
    selectedSourceId ?? runResult?.importSourceId ?? null;
  const importSourceTypeLabel = useMemo(() => {
    if (activeImportSourceId === null) {
      return null;
    }
    const fromCatalogue = sources.find(
      (source) => source.id === activeImportSourceId,
    );
    return fromCatalogue?.displayName ?? activeImportSourceId;
  }, [activeImportSourceId, sources]);
  const showForm = !normaliseRun.isPending && runResult === undefined;
  const submitError =
    normaliseRun.isError && normaliseRun.error instanceof Error
      ? normaliseRun.error.message
      : null;
  const activeFileName =
    pdfFile?.name ?? normaliseRun.variables?.file.name ?? "Payslip PDF";

  const handleSourceChange = useCallback(
    (sourceId: string | null) => {
      normaliseRun.reset();
      setSelectedSourceId(sourceId);
      setPdfFile(null);
      setFileTypeError(null);
    },
    [normaliseRun],
  );

  const handlePdfFileChange = useCallback(
    (file: File | null) => {
      normaliseRun.reset();
      if (file === null) {
        setPdfFile(null);
        setFileTypeError(null);
        return;
      }

      const rejection = getClientPayPdfFileRejection(file);
      if (rejection !== null) {
        setPdfFile(null);
        setFileTypeError(rejection);
        return;
      }

      setFileTypeError(null);
      setPdfFile(file);
    },
    [normaliseRun],
  );

  const handleNormalise = useCallback(() => {
    if (selectedSourceId === null || pdfFile === null) {
      return;
    }

    normaliseRun.mutate({
      importSourceId: selectedSourceId,
      file: pdfFile,
      payPeriodLabel: payPeriodLabel.trim() !== "" ? payPeriodLabel.trim() : undefined,
    });
  }, [normaliseRun, payPeriodLabel, pdfFile, selectedSourceId]);

  const resetAfterRun = useCallback(() => {
    normaliseRun.reset();
    artifacts.resetDownload();
    setPdfFile(null);
    setFileTypeError(null);
  }, [artifacts, normaliseRun]);

  const handleUploadAnother = resetAfterRun;
  const handleReturnToUpload = resetAfterRun;

  const artifactDownloadError =
    artifacts.downloadError instanceof Error
      ? artifacts.downloadError.message
      : null;

  const downloadForRun = useCallback(
    (
      kind: "detail" | "summary" | "super" | "issues" | "bundle",
    ) => {
      if (runResult === undefined) {
        return;
      }
      artifacts.downloadArtifact({
        runId: runResult.runId,
        kind,
        importFileName: activeFileName,
      });
    },
    [activeFileName, artifacts, runResult],
  );

  return (
    <div className={s.flow} aria-label="Client pay-run normalisation">
      {showForm && (
        <>
          <ImportSourceSection
            sources={sources}
            isLoading={isCatalogueLoading}
            loadError={catalogueLoadError}
            selectedSourceId={selectedSourceId}
            onSourceChange={handleSourceChange}
            payPeriodLabel={payPeriodLabel}
            onPayPeriodLabelChange={setPayPeriodLabel}
          />

          <PayRunPdfUploadSection
            sectionRef={uploadSectionRef}
            sourceSelected={selectedSource !== null}
            sourceDisplayName={selectedSource?.displayName ?? null}
            payPeriodLabel={payPeriodLabel}
            file={pdfFile}
            fileTypeError={fileTypeError}
            onFileChange={handlePdfFileChange}
            onNormalise={handleNormalise}
            isNormalising={normaliseRun.isPending}
            submitError={submitError}
          />
        </>
      )}

      {normaliseRun.isPending && importSourceTypeLabel !== null && (
        <NormalisationProgressPanel
          importSourceType={importSourceTypeLabel}
          fileName={activeFileName}
        />
      )}

      {artifactDownloadError !== null && runResult !== undefined && (
        <div className={s.artifactError} role="alert">
          {artifactDownloadError}
        </div>
      )}

      {runResult !== undefined && isNormalisationRunSuccess(runResult) && (
        <NormalisationCompletePanel
          result={runResult}
          importSourceType={
            importSourceTypeLabel ?? runResult.importSourceId
          }
          onUploadAnother={handleUploadAnother}
          onDownloadComponent={() => downloadForRun("detail")}
          onDownloadGross={() => downloadForRun("summary")}
          onDownloadSuper={() => downloadForRun("super")}
          onDownloadIssues={
            runResult.issues !== undefined && runResult.issues.length > 0
              ? () => downloadForRun("issues")
              : undefined
          }
          onDownloadBundle={() => downloadForRun("bundle")}
          isDownloading={artifacts.isDownloading}
        />
      )}

      {runResult !== undefined && isNormalisationRunBlocked(runResult) && (
        <NormalisationBlockedPanel
          result={runResult}
          onReturnToUpload={handleReturnToUpload}
          onDownloadIssues={() => downloadForRun("issues")}
          isDownloadingIssues={artifacts.isDownloading}
        />
      )}
    </div>
  );
}
